"use strict";

import jQuery from 'jquery';

/**
 * @typedef gc.gis.map.GCMapSize
 * @type {number[]}
 * @property {number} 0 width
 * @property {number} 1 height
 */

/**
 * @typedef gc.gis.map.GCMapExtent
 * @type {number[]}
 * @property {number} 0 xMin
 * @property {number} 1 yMin
 * @property {number} 2 xMax
 * @property {number} 3 yMax
 */

/**
 * @typedef gc.gis.map.GCMapScale
 * @type {Object}
 * @property {string} label Label, z.B.: '1:10.000'
 * @property {number} value Maßstabszahl, s.o.: 10000
 */

/**
 * @typedef gc.gis.map.GCMapProjection
 * @type {Object}
 * @property {string} label Label, z.B.: 'ETRS89 / UTM zone 32N'
 * @property {string} code EPSG-Code, s.o.: 'EPSG:25832'
 */

/**
 * @typedef gc.gis.map.GCMapViewport
 * @type {Object}
 * @property {number} rotation Drehwinkel der Karte in Grad
 * @property {string} geometry WKT-Polygon des sichtbaren Ausschnitts
 * @property {number[]} [position] Koordinaten des Zentrums des sichtbaren Ausschnitts
 * @property {string} projection Kartenprojektion
 */

/**
 * Karten-Events
 *
 * @enum {string}
 */
gc.gis.map.GCMapEvent = {
	/**
	 * @event gc.gis.map.GCMapEvent#layer:add
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer[]} layers Liste der hinzugefügten Layer
	 */
	LAYER_ADDED:					'layer:add',
	/**
	 * @event gc.gis.map.GCMapEvent#layer:remove
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer[]} layers Liste der gelöschten Layer
	 */
	LAYER_REMOVED:					'layer:remove',
	/**
	 * @event gc.gis.map.GCMapEvent#layer:change
	 * @type {Object}
	 */
	LAYER_CHANGED:					'layer:change',
	/**
	 * @event gc.gis.map.GCMapEvent#layer:move
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer} layer Bewegter Layer
	 * @property {number[]} source Quellindex
	 * @property {number[]} target Zielindex
	 */
	LAYER_MOVED:					'layer:move',
	/**
	 * @event gc.gis.map.GCMapEvent#update:schedule
	 * @type {Object}
	 * @property {number} timeout Zeit in Millisekunden
	 */
	LAYER_TREE_UPDATE_SCHEDULED:	'update:schedule',
	/**
	 * @event gc.gis.map.GCMapEvent#update:schedule
	 * @type {Object}
	 * @property {boolean} forced Wurde das Update manuell gestoppt?
	 */
	LAYER_TREE_UPDATE_CANCELLED:	'update:cancel',
	/**
	 * @event gc.gis.map.GCMapEvent#update:finish
	 * @type {Object}
	 */
	LAYER_TREE_UPDATE_FINISHED: 	'update:finish',
	/**
	 * @event gc.gis.map.GCMapEvent#loading:start
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer} [layer] Layerinstanz.
	 */
	LAYER_LOADING_STARTED:			'loading:start',
	/**
	 * @event gc.gis.map.GCMapEvent#loading:end
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer} [layer] Layerinstanz.
	 */
	LAYER_LOADING_ENDED:			'loading:end',
	/**
	 * @event gc.gis.map.GCMapEvent#loading:error
	 * @type {Object}
	 * @property {Error} error Fehlerobjekt
	 * @property {gc.gis.layer.GCAbstractLayer} [layer] Layerinstanz.
	 */
	LAYER_LOADING_ERROR:			'loading:error',
	/**
	 * @event gc.gis.map.GCMapEvent#bglayer:add
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer[]} layers Liste der hinzugefügten Layer
	 */
	BACKGROUND_LAYER_ADDED:			'bglayer:add',
	/**
	 * @event gc.gis.map.GCMapEvent#bglayer:remove
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer[]} layers Liste der gelöschten Layer
	 */
	BACKGROUND_LAYER_REMOVED:		'bglayer:remove',
	/**
	 * Angegebener Trackinglayer dem Kartenobjekt zugefügt.
	 * @event gc.gis.map.GCMapEvent#trackinglayer:add
	 * @type {Object}
	 * @property {gc.gis.layer.GCTrackingLayer} layer Layerinstanz.
	 */
	TRACKINGLAYER_ADDED:			'trackinglayer:add',
	/**
	 * Angegebener Trackinglayer vom Kartenobjekt entfernt.
	 * @event gc.gis.map.GCMapEvent#trackinglayer:remove
	 * @type {Object}
	 * @property {gc.gis.layer.GCTrackingLayer} layer Layerinstanz.
	 */
	TRACKINGLAYER_REMOVED:			'trackinglayer:remove',
	/**
	 * Alle Trackinglayer vom Kartenobjekt entfernt.
	 * @event gc.gis.map.GCMapEvent#trackinglayer:removeall
	 */
	TRACKINGLAYER_REMOVED_ALL:      'trackinglayer:removeall',
	/**
	 * Wird ausgelöst, wenn eine Änderung am Viewport stattfindet. Dies passiert bei einer animierten Aktion (z.B. Zoom)
	 * mehrfach.
	 *
	 * @event gc.gis.map.GCMapEvent#change:viewport
	 * @type {Object}
	 * @property {gc.gis.map.GCMapViewport} viewport Neuer Viewport.
	 */
	VIEWPORT_CHANGED:				'change:viewport',
	/**
	 * Wird ausgelöst, wenn eine Änderung am Viewport stattgefunden hat. Eine Animation wird dabei nicht berücksichtigt,
	 * erst wenn die Karte im finalen Zustand ist erfolgt die Auslösung des Events.
	 *
	 * @event gc.gis.map.GCMapEvent#change:viewportchangeend
	 * @type {Object}
	 * @property {gc.gis.map.GCMapViewport} viewport Neuer Viewport.
	 */
	VIEWPORT_CHANGEEND:				'viewportchangeend',

    /**
     * @event gc.gis.map.GCMapEvent#click
     * @type {Object}
     */
    CLICK:							'click',
	/**
     * @event gc.gis.map.GCMapEvent#singleclick
     * @type {Object}
     */
	SINGLECLICK:					'singleclick',
    /**
     * @event gc.gis.map.GCMapEvent#doubleclick
     * @type {Object}
     */
    DOUBLECLICK:					'doubleclick',
	/**
	 * @event gc.gis.map.GCMapEvent#change:scale
	 * @type {Object}
	 * @property {number} [oldValue] Alter Wert
	 * @property {number} newValue Neuer Wert
	 */
	SCALE_CHANGED:					'change:scale',
	/**
	 * @event gc.gis.map.GCMapEvent#change:rotation
	 * @type {Object}
	 * @property {number} rotation Aktueller Rotationswinkel.
	 */
	ROTATION_CHANGED:				'change:rotation',
	/**
	 * @event gc.gis.map.GCMapEvent#change:projection
	 * @type {Object}
	 * @property {number} [oldValue] Alter Wert
	 * @property {number} newValue Neuer Wert
	 */
	PROJECTION_CHANGED:				'change:projection',
	/**
	 * @event gc.gis.map.GCMapEvent#interaction:activated
	 * @type {Object}
	 * @property {*} interaction Kartenbibliotheksspezifisch
	 */
	INTERACTION_ACTIVATED:			'interaction:activated',
	/**
	 * @event gc.gis.map.GCMapEvent#interaction:deactivated
	 * @type {Object}
	 * @property {*} interaction Kartenbibliotheksspezifisch
	 */
	INTERACTION_DEACTIVATED:		'interaction:deactivated',
	/**
	 * @event gc.gis.map.GCMapEvent#pointer:move
	 * @type {Object}
	 * @property {*} interaction Kartenbibliotheksspezifisch
	 */
	POINTER_MOVE:                   'pointer:move',
	/**
	 * @event gc.gis.map.GCMapEvent#mouse:enter
	 * @type {Object}
	 * @property {*} Browser-Event
	 */
	MOUSE_ENTER:                    'mouse:enter',
	/**
	 * @event gc.gis.map.GCMapEvent#mouse:leave
	 * @type {Object}
	 * @property {*} Browser-Event
	 */
	MOUSE_LEAVE:                    'mouse:leave'
};

/**
 * @classdesc
 * Wrapperklasse für Kartenobjekte. Kapselt alle öffentlichen
 * Kartenfunktionalitäten.
 *
 * @class
 * @extends gc.gis.GCWrappable
 * @memberof gc.gis
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen (siehe auch {@link gc.gis.map.GCAbstractMap})
 * @param {string} options.target HTML-ID des Containerelements
 *
 * @param {gc.gis.map.GCMapExtent} options.maxExtent maximales Ausdehnungsrechteck, welches die Kartenansicht beschränkt
 * @param {boolean} [options.constrainOnlyCenter=false] optionales Flag, welches eine "weiche" Einschränkung auf das maxExtent setzt,
 * 														sodass nur der Kartenmittelpunkt innerhalb des Ausdehnungsrechteck "eingesperrt"
 * 														wird.
 * @param {Array<Object<string,number>>} [options.availableScales] Verfügbare Maßstäbe
 */
gc.gis.map.GCMapView = function(options)
{
	gc.gis.GCWrappable.call( this );

	/**
	 * @public
	 * @type string
	 */
	this.target           = options.target;
	/**
	 * @protected
	 * @type {gc.gis.map.GCMapExtent}
	 */
	this._max_extent      = options.maxExtent;
	/**
	 * @protected
	 * @type {Array<Object<string,string>>} {code: string,label: string}
	 */
	this._available_projections = options.availableProjections;
	/**
	 * @protected
	 * @type {Array<Object<string,number>>} {label: string, value: number}
	 */
	this._available_scales = options.availableScales;
	/**
	 * @protected
	 * @type gc.gis.layer.GCGroupLayer
	 */
	this._root_group      = new gc.gis.layer.GCGroupLayer({
		visible: true,
		defaultLabel: 'root'	// nur für Debugging wird nicht ausgegeben
	});
	/**
	 * @protected
	 * @type number
	 */
	this._loading_timeout = options.loadingTimeout || 2000;
	/**
	 * @protected
	 * @type gc.gis.layer.GCAbstractLayer[]
	 */
	this._loading_queue   = [];
	/**
	 * @protected
	 * @type gc.gis.control.GCAbstractMapControl[]
	 */
	this._controls        = options.controls || [];

	/** Workaround: Größe des Map-Containers für IE <= 11 korrigieren */
	this.__correctMapContainerSize();

	/**
	 * @protected
	 * @type gc.gis.map.GCAbstractMap
	 */
	this._impl            = gc.gis.iface.mapFactory.createMap(this, options);
	/**
	 * @protected
	 * @type gc.gis.map.GCLayerTreeManager
	 */
	this._layer_manager   = new gc.gis.map.GCLayerTreeManager({
		map: this
	});
	/**
	 * @protected
	 * @type gc.gis.map.GCBackgroundLayerManager
	 */
	this._bglayer_manager   = new gc.gis.map.GCBackgroundLayerManager({
		map: this
	});

	this.__initEvents();
};
gc.gis.map.GCMapView.prototype = Object.create( gc.gis.GCWrappable.prototype );

/**
 * Initialisiert Eventlistener.
 *
 * @private
 */
gc.gis.map.GCMapView.prototype.__initEvents = function()
{
	var self = this;

	// Resize trigger
	jQuery('#'+ this.target).resize( function() {
		if( self.__resize_timer )
		{
			clearTimeout( self.__resize_timer );
			// gc.gis.iface.log.debug( self, 'GCMapView: Resize update cancelled ... ');
		}

		/**
         * @private
		 * @type {number}
         */
		self.__resize_timer = setTimeout( function() {
			try
			{
				/** Workaround: Größe des Map-Containers für IE <= 11 korrigieren */
				self.__correctMapContainerSize();

				if( self.getMap() )
				{
					self.getMap().updateSize();
				}
			}
			catch( err )
			{
				gc.gis.iface.log.error( self, err );
			}
			finally
			{
				self.__resize_timer = undefined;
			}
		}, 100 );
	});

	// Klicks innerhalb der Karte
    this.getMap().on(gc.gis.map.GCMapEvent.CLICK, this, function(evt) {
        self._fireEvent(gc.gis.map.GCMapEvent.CLICK, evt);
    });
    this.getMap().on(gc.gis.map.GCMapEvent.DOUBLECLICK, this, function(evt) {
        self._fireEvent(gc.gis.map.GCMapEvent.DOUBLECLICK, evt);
    });
    this.getMap().on(gc.gis.map.GCMapEvent.SINGLECLICK, this, function(evt) {
        self._fireEvent(gc.gis.map.GCMapEvent.SINGLECLICK, evt);
    });

    // Mausbewegungen
	this.getMap().on(gc.gis.map.GCMapEvent.MOUSE_ENTER, this, function(evt) {
		self._fireEvent(gc.gis.map.GCMapEvent.MOUSE_ENTER, evt);
	});
	this.getMap().on(gc.gis.map.GCMapEvent.MOUSE_LEAVE, this, function(evt) {
		self._fireEvent(gc.gis.map.GCMapEvent.MOUSE_LEAVE, evt);
	});
	this.getMap().on(gc.gis.map.GCMapEvent.POINTER_MOVE, this, function(evt) {
		self._fireEvent(gc.gis.map.GCMapEvent.POINTER_MOVE, evt);
	});

	// Viewport-Änderungen
	this.getMap().on( gc.gis.map.GCMapEvent.SCALE_CHANGED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.SCALE_CHANGED, evt );
	});
	this.getMap().on( gc.gis.map.GCMapEvent.ROTATION_CHANGED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.ROTATION_CHANGED, evt );
	});
	this.getMap().on( gc.gis.map.GCMapEvent.VIEWPORT_CHANGED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.VIEWPORT_CHANGED, evt );
	});
	this.getMap().on( gc.gis.map.GCMapEvent.VIEWPORT_CHANGEEND, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.VIEWPORT_CHANGEEND, evt );
	});

	// Interaktionen
	this.getMap().on( gc.gis.map.GCMapEvent.INTERACTION_ACTIVATED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.INTERACTION_ACTIVATED, evt );
	});
	this.getMap().on( gc.gis.map.GCMapEvent.INTERACTION_DEACTIVATED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.INTERACTION_DEACTIVATED, evt );
	});

	// Update-Timer
	this.getMap().on( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_SCHEDULED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_SCHEDULED, evt );
	});
	this.getMap().on( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_CANCELLED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_CANCELLED, evt );
	});
	this._layer_manager.on( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_SCHEDULED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_SCHEDULED, evt );
	});
	this._layer_manager.on( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_CANCELLED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_CANCELLED, evt );
	});
	this._layer_manager.on( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_FINISHED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_FINISHED, evt );

		if (self.getReallyVisibleLayers().length > 0)
		{
			jQuery('#'+ self.target ).removeClass('gc-empty');
		}
		else
		{
			jQuery('#'+ self.target ).addClass('gc-empty');
		}
	});

	// Layer-Bewegungen
	this._root_group.on( gc.gis.map.GCMapEvent.LAYER_ADDED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_ADDED, evt );
	});
	this._root_group.on( gc.gis.map.GCMapEvent.LAYER_REMOVED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_REMOVED, evt );
	});
	this._root_group.on( gc.gis.map.GCMapEvent.LAYER_MOVED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_MOVED, evt );
	});

	// empty-flag, wenn keine Layer geladen
	this.on( gc.gis.map.GCMapEvent.LAYER_ADDED, this, function(evt ) {
		if( self.getLayers().length > 0 )
		{
			jQuery('#'+ self.target ).removeClass('gc-empty');
		}
	});
	this.on( gc.gis.map.GCMapEvent.LAYER_REMOVED, this, function(evt ) {
		if( self.getLayers().length <= 0 )
		{
			jQuery('#'+ self.target ).addClass('gc-empty');
		}
	});
};

/**
 * Größe des Kartenfensters aktualisieren bzw. korrigieren.
 *
 * Workaround für IE <= 11:
 * Im IE (kleiner Edge) gibt es einen BUG bei der Berechnung von Box-Größen und der Eigenschaft
 * "display: table". Daher wird hier beim resize die Größe der Kartenfenster-Box manuell gesetzt.
 * Bei anderen Browsern erfolgt die Anpassung nicht, da die Größen korrekt ermittelt werden.
 *
 * @private
 */
gc.gis.map.GCMapView.prototype.__correctMapContainerSize = function ()
{
    /**
	 *  Workaround für IE <= 11 und Safari auf iPad:
	 *  Im IE (kleiner Edge) gibt es einen BUG bei der Berechnung von Box-Größen und der Eigenschaft
	 *  "display: table". Daher wird hier beim resize durch Aufruf der Funktion die Größe der Kartenfenster-Box
	 *  manuell gesetzt. Bei anderen Browsern erfolgt die Anpassung nicht, da die Größen korrekt ermittelt werden.
	 */
	var elMapView = jQuery('#' + this.target);
	var elGisMain = elMapView.parent();
	// FIXME: bei festen Höhenangaben z.B. Übersichtskarte ist eine Apssung nicht notwendig,
	// zudem stimmt die Höhenbestimmung nicht zu 100%, da Abstände und Border dazu kommen, daher darf "> 4"-Prüfung
	// nicht unterschritten werden
	if (Math.abs(elMapView.height() - elGisMain.height()) > 4) {
		elMapView.height(elGisMain.height());
		gc.gis.iface.log.info(this, 'Kartenhöhe korrigiert für "#' + this.target + '" auf '
			+ elGisMain.height() + '.', false);
	}

    /**
	 * FIXME: das kann es doch nicht sein
     *  Workaround High DPI/Retina:
	 *  Absolute Höhe setzen, damit Höhe korrigiert wird.
	 */
    if (gc.gis.iface.env.devicePixelRatio > 1) {
    	elMapView.height(elMapView.height());
    }
};

/**
 * Lädt Kartenkontrollelemente.
 *
 * @public
 * @param {gc.gis.control.GCAbstractMapControl[]} controls
 */
gc.gis.map.GCMapView.prototype.setControls = function(controls)
{
	this._controls = controls;

	for (let i = 0, ii = controls.length; i < ii; i++)
	{
		this._initAndAttachControl(controls[i]);
	}
};

/**
 * Lädt ein Kartenkontrollelement.
 *
 * @public
 * @param {gc.gis.control.GCAbstractMapControl} control
 */
gc.gis.map.GCMapView.prototype._initAndAttachControl = function(control)
{
	try
	{
		control.init();
		const element = control.getElement();

		if (element)
		{
			let target;
			if (control._target)
			{
				this._createControlTargetIfNotExists(control);
				target = control._target;
			}
			else
			{
				target = jQuery('#'+ this.target).parent();
			}

			gc.gis.iface.ui.attach(element, {
				target: target,
				order:  control.order
			});
		}
	}
	catch (err)
	{
		gc.gis.iface.log.error(this, err);
	}
};

/**
 * @param {gc.gis.control.GCAbstractMapControl} control
 * @private
 */
gc.gis.map.GCMapView.prototype._createControlTargetIfNotExists = function(control)
{
	const targetNotExists = typeof control._target === 'string'
		&& jQuery(control._target).length === 0;

	if (targetNotExists)
	{
		jQuery('#' + this.target ).parent().append(jQuery('<div>', {
			id: control._target.replace('#', '')
		}));
	}
};

/**
 * Liefert ein Kartenkontrollelement, sofern es geladen wurde, ansonsten <code>null</code>.
 *
 * @public
 * @param {function} control_type
 */
gc.gis.map.GCMapView.prototype.getControlByType = function(control_type)
{
	for (var i=0; i<this._controls.length; i++)
	{
		if (this._controls[i] instanceof control_type)
		{
			return this._controls[i];
		}
	}

	return null;
};

/**
 * Liefert das Snap-Kartenkontrollelement, sofern es geladen wurde, ansonsten <code>null</code>.
 *
 * @public
 */
gc.gis.map.GCMapView.prototype.getSnapControl = function()
{
	return this.getControlByType(gc.gis.control.OLSnap);
};

/**
 * Gibt die Kartenbibliothekswrapperinstanz zurück.
 *
 * @public
 * @return {gc.gis.map.GCAbstractMap}
 */
gc.gis.map.GCMapView.prototype.getMap = function()
{
	return this._impl;
};

/**
 * Gibt die Data-URL eines Canvas-Elements unterhalb von
 * {@link gc.gis.map.GCMapView#target} zurück (Screenshot).
 *
 * @public
 * @param {string} [opt_format='image/png'] optionales Bildformat
 * @param {int} [opt_width] optionale Breite des Bildes
 * @param {int} [opt_height] optionale Höhe des Bildes
 * @param {boolean} [opt_aspectRatio=true] optionales Flag, ob Seitenverhältnis beibehalten werden soll
 * @return {string} Canvas.toDataURL( format )
 * @throws {gc.gis.error.GCError}
 */
gc.gis.map.GCMapView.prototype.getCanvasDump = function (opt_format, opt_width, opt_height, opt_aspectRatio)
{
	var format = opt_format || 'image/png';
	var aspectRatio = opt_aspectRatio || true;
	var width = opt_width || 0;
	var height = opt_height || 0;

	var canvas = jQuery('#'+ this.target ).find('canvas')[0];
	if( canvas )
	{
		// ggf. skalieren
		if (width > 0 && height > 0 && canvas.width && canvas.height && canvas.width > 0 && canvas.height > 0) {
			var cW = canvas.width;
			var cH = canvas.height;
			var canvas2, scale;

			// ggf. Seitenverhältnis beibehalten
			if (aspectRatio) {
				scale = Math.min((width / canvas.width), (height / canvas.height));
				width = Math.round(canvas.width * scale);
				height = Math.round(canvas.height * scale);
			}

			// resampling: reduce the size in steps to allow the limited
			// interpolation range to cover enough pixels for sampling
			while (true) {
				cW /= 2;
				cH /= 2;
				if (cW < width) cW = width;
				if (cH < height) cH = height;

				canvas2 = document.createElement('canvas');
				canvas2.width = cW;
				canvas2.height = cH;
				canvas2.getContext('2d').drawImage(canvas, 0, 0, cW, cH);
				canvas = canvas2;

				if (cW <= width || cH <= height) {
					break;
				}
			}
		}

		return canvas.toDataURL(format);
	}
	else
	{
		throw new gc.gis.error.GCError('Kartenabbild konnte nicht erstellt werden.');
	}
};

/**
 * Gibt die Data-URL eines Canvas-Elements unterhalb von {@link gc.gis.map.GCMapView#target} mit den Inhalten der
 * Tracking-Layer zurück (Screenshot).
 *
 * @public
 * @method
 * @param {string} [opt_format='image/png'] optionales Bildformat (image/png oder image/jpeg)
 * @return {JQueryPromise<T>} ein jQuery Deferred's Promise object {@link http://api.jquery.com/Types/#Promise}
 * @throws {gc.gis.error.GCError}
 */
gc.gis.map.GCMapView.prototype.getCanvasTrackingLayerDump = function(opt_format)
{
	// FIXME: anstatt JQueryPromise die konkreten Daten zurückgeben (Problem ansynchrones Rendering)
	var format = opt_format || 'image/png';
	return this.getMap().dumpCanvasTrackingLayer(format);
};


/**
 * Gibt die Data-URL eines Canvas-Elements innerhalb einer Bounding-Box und in der gewünschten Größe unterhalb mit den
 * Inhalten der Tracking-Layer zurück (Screenshot).
 *
 * @public
 * @method
 * @param {gc.gis.map.GCMapExtent} box Boundingbox
 * @param {Number[]} size Breite und Höhe des Screenshots z.B. [200, 100]
 * @param {string} [opt_format='image/png'] optionales Bildformat (image/png oder image/jpeg)
 * @return {JQueryPromise<T>} ein jQuery Deferred's Promise object {@link http://api.jquery.com/Types/#Promise}
 */
gc.gis.map.GCMapView.prototype.dumpCanvasTrackingLayerByBox = function (box, size, opt_format)
{
	// FIXME: anstatt JQueryPromise die konkreten Daten zurückgeben (Problem ansynchrones Rendering)
	var format = opt_format || 'image/png';
	return this.getMap().dumpCanvasTrackingLayerByBox(box, size, format);
};

/**
 * Erzwingt das Neuladen der Karte. Dabei wird die interne Layer-Repäsentation neu aufgebaut.
 * Hinweis: Der Aufruf dieser Methode zieht nicht zwangsläufig das Neuladen der Layer nach sich. Wenn sich der interne
 * Layer-Tree nicht geändet hat, sieht die Kartenbibliothek nicht zwangsläufig die Veranlassung die Grafiken neu zu
 * laden. Wird dies gewünscht, z.B. nach dem Speichern beim Editieren, kann die Methode reloadMap() aufgerufen werden.
 *
 * @public
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.updateMap = function()
{
	this._layer_manager.forceUpdate();

	return this;
};

/**
 * Erzwingt das Neuzeichnen der Karte. Dabei werden die Daten der Layer neu über den jeweiligen Dienst geladen.
 *
 * @public
 * @param {boolean} [opt_image=true] Flag Image-Layer neuladen
 * @param {boolean} [opt_tile=true] Flag Tile-Layer neuladen
 * @param {boolean} [opt_vector=true] Flag Vector-Layer neuladen
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.reloadMap = function(opt_image, opt_tile, opt_vector)
{
	this.getMap().reloadLayers(opt_image, opt_tile, opt_vector);

	return this;
};

/**
 * Gibt den internen Wurzellayer des Layerbaums zurück.
 *
 * @public
 * @return {gc.gis.layer.GCGroupLayer} Wurzel-Gruppenlayer
 */
gc.gis.map.GCMapView.prototype.getRoot = function()
{
	return this._root_group;
};

/**
 * @public
 * @param {Object} layerOptions Optionen des Hintergrundlayers (siehe Optionen in
 *   {@link gc.gis.layer.GCAbstractLayer},
 *   {@link gc.gis.layer.GCLayer},
 *   {@link gc.gis.layer.GCRasterLayer} und
 *   {@link gc.gis.layer.GCVectorLayer})
 * @param {Number} [opt_drawOrder]
 * @param {string} [opt_previewClassName]
 * @param {string} [opt_previewImage]
  */
gc.gis.map.GCMapView.prototype.loadBackgroundLayer = function(layerOptions, opt_drawOrder, opt_previewClassName, opt_previewImage)
{
	let layer;
	if (layerOptions.type)
	{
		layer = this._createLayerFromOptions(layerOptions);
		this.addBackgroundLayer(layer, opt_drawOrder, opt_previewClassName, opt_previewImage);
	}
};

/**
 * @param {gc.gis.layer.GCAbstractLayer} layer
 * @param {Number} [opt_drawOrder]
 * @param {string} [opt_previewClassName]
 * @param {string} [opt_previewImage]
 */
gc.gis.map.GCMapView.prototype.addBackgroundLayer = function(layer, opt_drawOrder, opt_previewClassName, opt_previewImage)
{
	const event = {
		layers: [layer],
		drawOrders: [],
		previewClassNames: [],
		previewImages: []
	};
	if (undefined !== opt_drawOrder)
	{
		event.drawOrders = [opt_drawOrder];
	}
	if (undefined !== opt_previewClassName)
	{
		event.previewClassNames = [opt_previewClassName];
	}
	if (undefined !== opt_previewImage)
	{
		event.previewImages = [opt_previewImage];
	}
	this._fireEvent(gc.gis.map.GCMapEvent.BACKGROUND_LAYER_ADDED, event);
};

/**
 * @param {gc.gis.layer.GCAbstractLayer} layer
 */
gc.gis.map.GCMapView.prototype.removeBackgroundLayer = function(layer)
{
	this._fireEvent(gc.gis.map.GCMapEvent.BACKGROUND_LAYER_REMOVED, {
		layers: [layer]
	});
};

/**
 * @return {gc.gis.layer.GCAbstractLayer}
 */
gc.gis.map.GCMapView.prototype.getVisibleBackgroundLayer = function()
{
	return this._bglayer_manager.getVisibleBackgroundLayer();
};

/**
 * @public
 * @param {Object} layerOptions Optionen (siehe Optionen in
 *   {@link gc.gis.layer.GCAbstractLayer},
 *   {@link gc.gis.layer.GCLayer},
 *   {@link gc.gis.layer.GCRasterLayer} und
 *   {@link gc.gis.layer.GCVectorLayer})
 * @param {gc.gis.layer.GCGroupLayer} [opt_group] Gruppenlayer, zu dem der zu ladende
 *   Layer hinzugefügt werden soll
 */
gc.gis.map.GCMapView.prototype.loadLayer = function(layerOptions, opt_group)
{
	const self = this;
	let layer;

	if (layerOptions.type)
	{
		layer = this._createLayerFromOptions(layerOptions);

		if (layer)
		{
			if (opt_group)
			{
				opt_group.addLayer(layer);
			}
			else
			{
				self.addLayer(layer);
			}

			if (layer instanceof gc.gis.layer.GCGroupLayer)
			{
				// items ist eine Liste von vollständigen Layerkonfigurationen
				if (layerOptions.items)
				{
					for(let i = 0; i<layerOptions.items.length; i++)
					{
						if(layerOptions.items[i].checked !== false)
						{
							self.loadLayer(layerOptions.items[i], layer);
						}
					}
				}
				// item_count => Child vorhanden, aber Layerkonfiguration muss noch geladen werden
				// nur für interne Layer (Kartenkatalog siehe Parameter ID)
				else if (layerOptions.item_count)
				{
					const url = gc.gis.GCUrl.buildUrl(gc.gis.iface.requests.catalogGetCategoryUrl, {id: layerOptions.id});
					gc.gis.iface.request.ajax({
						label: 'Lade Ebene ... ',
						context: this,
						url: url,
						dataType: gc.gis.util.GCRequestManagerDataType.JSON,
						success: function(data)
						{
							// Ergebnis "Reversen", da OL Layer intern umgekehrt organisiert, als die Legende aufgebaut ist
							for(let i = data.results.length - 1; i>=0; i--)
							{
								if (data.results[i].items)
								{
									data.results[i].items.reverse();
								}
								self.loadLayer(data.results[i], layer);
							}
							self.updateMap();
						}
					});
				}
			}
		}
	}
};

/**
 * @private
 */
gc.gis.map.GCMapView.prototype._createLayerFromOptions = function(layerOptions)
{
	let layer;
	if(layerOptions.type === 'group')
	{
		layer = new gc.gis.layer.GCGroupLayer(layerOptions);
	}
	else if(layerOptions.type === 'wms')
	{
		layer = new gc.gis.layer.GCRasterLayer(layerOptions);
	}
	else if(layerOptions.type === 'wfs')
	{
		layer = new gc.gis.layer.GCVectorLayer(layerOptions);
	}
	else
	{
		gc.gis.iface.log.error( this, new gc.gis.error.GCError('Ebenentyp nicht unterstützt: '+ layerOptions.type ) );
	}

	return layer;
};

/**
 * Fügt dem internen Wurzellayer einen Layer hinzu.
 *
 * @public
 * @param {gc.gis.layer.GCAbstractLayer} layer Hinzuzufügender Layer
 * @param {number} [opt_index] Optionale Einfügeposition
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.addLayer = function( layer, opt_index )
{
	if( typeof opt_index === 'number')
	{
		this._root_group.insertLayerAt( layer, opt_index );
	}
	else
	{
		this._root_group.addLayer( layer );
	}

	return this;
};

/**
 * Fügt dem internen Wurzellayer mehrere Layer hinzu.
 *
 * @public
 * @param {gc.gis.layer.GCAbstractLayer[]} layers Hinzuzufügende Layer
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.addLayers = function( layers )
{
	this._root_group.addLayers( layers );

	return this;
};

/**
 * Entfernt einen Layer aus dem internen Wurzellayer.
 *
 * @public
 * @param {gc.gis.layer.GCAbstractLayer} layer Zu entfernender Layer
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.removeLayer = function( layer )
{
	this._root_group.removeLayer( layer );

	return this;
};

/**
 * Entfernt alle Layer aus dem internen Wurzellayer.
 *
 * @public
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.removeAllLayers = function()
{
	this._root_group.removeAllLayers();

	return this;
};

/**
 * Verschiebt einen Layer im Layerbaum
 *
 * @public
 * @param {gc.gis.layer.GCAbstractLayer} layer Zu verschiebender Layer
 * @param {number[]} to Index-Pfad zum Ziel
 * @return {gc.gis.map.GCMapView} this
 * @throws {gc.gis.error.GCError} Wenn Zielgruppe nicht gefunden wurde
 * @fires gc.gis.map.GCMapEvent#layer:move
 */
gc.gis.map.GCMapView.prototype.moveLayer = function(layer, to)
{
	var target_index = to.splice(-1, 1)[0];
	var target_layer = this._root_group.findLayer(to);

	if(to.length === 0 && !target_layer)
	{
		target_layer = this._root_group;
	}

	if(target_layer)
	{
		// insertLayerAt feuert gc.gis.map.GCMapEvent.LAYER_ADDED-Event => dass muss hier aber unterdrückt werden,
		// da explizites gc.gis.map.GCMapEvent.LAYER_MOVED-Event gefeuert wird
		target_layer.insertLayerAt(layer, target_index, false);
		this._fireEvent(gc.gis.map.GCMapEvent.LAYER_MOVED, {
            source_layer: layer,
			target_layer: target_layer,
            index: target_index
		});
	}
	else
	{
		throw new gc.gis.error.GCError('Zielgruppe nicht gefunden.');
	}

	return this;
};

/**
 * Verschiebt eine Liste von Layern im Layerbaum.
 *
 * @public
 * @param {gc.gis.layer.GCAbstractLayer[]} layers Zu verschiebende Layer
 * @param {number[]} to Index-Pfad zum Ziel
 * @return {gc.gis.map.GCMapView} this
 * @throws {gc.gis.error.GCError} Wenn Zielgruppe nicht gefunden wurde
 * @fires gc.gis.map.GCMapEvent#layer:move
 */
gc.gis.map.GCMapView.prototype.moveLayerList = function(layers, to)
{
    var target_index = to.splice(-1, 1)[0];
    var target_layer = this._root_group.findLayer(to);

    if(to.length === 0 && !target_layer)
    {
        target_layer = this._root_group;
    }

    if(target_layer)
    {
        // insertLayerAt feuert gc.gis.map.GCMapEvent.LAYER_ADDED-Event => dass muss hier aber unterdrückt werden,
        // da explizites gc.gis.map.GCMapEvent.LAYER_MOVED-Event gefeuert wird
		for (var i=layers.length-1; i>=0; i--)
		{
            target_layer.insertLayerAt(layers[i], target_index, false);
		}

        this._fireEvent(gc.gis.map.GCMapEvent.LAYER_MOVED, {
            source_layers: layers,
            target_layer: target_layer,
            index: target_index
        });
    }
    else
    {
        throw new gc.gis.error.GCError('Zielgruppe nicht gefunden.');
    }

    return this;
};

/**
 * Gibt einen Layer anhand seines Indexes im Layerbaum zurück.
 *
 * @public
 * @param {number[]} index Index-Pfad des abzurufenden Layers
 * @return {gc.gis.layer.GCAbstractLayer}
 */
gc.gis.map.GCMapView.prototype.getLayer = function( index )
{
	return this._root_group.findLayer( index );
};

/**
 * Gibt alle Layer des internen Wurzellayers zurück.
 *
 * @public
 * @return {gc.gis.layer.GCAbstractLayer[]}
 */
gc.gis.map.GCMapView.prototype.getLayers = function()
{
	return this._root_group.getLayers();
};

/**
 * Gibt eine Objektrepräsentation des Layerbaums zurück.
 *
 * @public
 * @return {Object} Objektrepräsentation des Layerbaums
 */
gc.gis.map.GCMapView.prototype.getLayerConfigs = function()
{
	var configs = [];
	var layers = this.getLayers();
	for( var i=0; i<layers.length; i++ )
	{
		configs.push( layers[i].toObject() );
	}
	return configs;
};

/**
 * Gibt eine flache Liste der sichtbaren Layer zurück.
 *
 * @public
 * @return {gc.gis.layer.GCAbstractLayer[]} Flache Liste der sichtbaren Layer
 */
gc.gis.map.GCMapView.prototype.getVisibleLayers = function()
{
	return this._root_group.getLayersRecursive( function( layer ) {
		return layer.isVisible();
	});
};

/**
 * Gibt eine flache Liste der sichtbaren Layer zurück. Dabei wird auch die Sichtbarkeit der Elternlayer dieses Layers
 * ausgewertet.
 *
 * @public
 * @method
 * @return {gc.gis.layer.GCAbstractLayer[]} Flache Liste der tatsächlich sichtbaren Layer
 */
gc.gis.map.GCMapView.prototype.getReallyVisibleLayers = function()
{
    return this._root_group.getLayersRecursive( function( layer ) {
        return layer.isReallyVisible();
    });
};

/**
 * Gibt eine flache Liste der ausgewählten Layer zurück.
 *
 * @public
 * @return {gc.gis.layer.GCAbstractLayer[]} Flache Liste der ausgewählten Layer
 */
gc.gis.map.GCMapView.prototype.getSelectedLayers = function()
{
	return this._root_group.getLayersRecursive( function( layer ) {
		return layer.isSelected();
	});
};

/**
 * Sammelt rekursiv alle bzw. alle dem Filter entsprechenden Unterlayer und gibt sie als <em>flache Liste</em> zurück.
 *
 * @public
 * @method
 * @param {gc.gis.layer.GCLayerFilterCallback} [opt_filter_func]  Optionale Filterfunktion
 * @return {gc.gis.layer.GCAbstractLayer[]} Unterlayer-Liste
 */
gc.gis.map.GCMapView.prototype.getLayersRecursive = function( opt_filter_func )
{
    return this._root_group.getLayersRecursive(opt_filter_func);
};

/**
 * Erzeugt eine Debug-Ausgabe des Layerbaums und gibt diese auf der Konsole aus.
 *
 * @public
 * @method
 */
gc.gis.map.GCMapView.prototype.debugLayerTree = function(layer, level)
{
	var dump = '';
	if (!layer)
	{
        dump += "ACHTUNG: Baum ist inverse zur Darstellung in der Legende:\n";
	}

	layer = layer || this._root_group;
	level = level || 0;

    dump += "  ".repeat(level) + "+ " + layer.getLabel();

	if (layer instanceof gc.gis.layer.GCGroupLayer)
	{
        var i, sub_layers = [];
        sub_layers = layer.getLayers();
        for (i = 0; i < sub_layers.length; i++)
        {
            dump += ("\n");
            dump += this.debugLayerTree(sub_layers[i], level + 1);
        }
    }
    return dump;
};

/**
 * (De-)Selektiert alle Layer im Layerbaum. Wird kein Selektierstatus übergeben,
 * wird die Auswahl invertiert.
 *
 * @private
 * @param {boolean} [opt_state] Selektierstatus
 */
gc.gis.map.GCMapView.prototype.__select = function( opt_state )
{
	//var layers = this.getLayers();
	var layers = this._root_group.getLayersRecursive( function( layer ) {
		return !layer.isSelected();
	});
	for( var i=0; i<layers.length; i++ )
	{
		// Invertieren, falls Parameter nicht gesetzt
		var state = opt_state;
		if( state !== true && state !== false )
		{
			state = !layers[i].isSelected();
		}
		layers[i].setSelected( state );
	}
};

/**
 * Selektiert alle Layer im Layerbaum
 *
 * @public
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.selectAll = function()
{
	this.__select( true );

	return this;
};

/**
 * Deselektiert alle Layer im Layerbaum
 *
 * @public
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.selectNone = function()
{
	var layers = this.getSelectedLayers();
	for( var i=0; i<layers.length; i++ )
	{
		layers[i].setSelected( false );
	}

	return this;
};

/**
 * Invertiert die aktuelle Layerauswahl.
 *
 * @public
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.selectInverse = function()
{
	this.__select();

	return this;
};

/**
 * Transformiert einen Extent zwischen Koordinatenreferenzsystemen.
 *
 * @public
 * @param {gc.gis.map.GCMapExtent} extent Ursprungsextent
 * @param {string} source_crs Startkoordinatenreferenzsystem
 * @param {string} target_crs Zielkoordinatenreferenzsystem
 * @return {gc.gis.map.GCMapExtent} Extent im Zielkoordinatenreferenzsystem
 */
gc.gis.map.GCMapView.prototype.transformExtent = function( extent, source_crs, target_crs )
{
	return this.getMap().transformExtent( extent, source_crs, target_crs );
};

/**
 * Passt den Kartenausschnitt anhand eines Extents an.
 *
 * @public
 * @param {gc.gis.map.GCMapExtent} extent
 *
 * @param {Object} [opt_options] Aufrufoptionen
 * @param {string} [opt_options.projection] optionale Projektion der übergebenen
 *   Geometrie. Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @param {boolean} [opt_options.constrain_resolution] die Auflösungen des Kartenfensters (im Prinzip verfügbare Maßstäbe)
 *   berücksichtigen. Es wird zur passenden Auflösung gezoomt.
 * @param {int} [opt_options.minscale] Maßstabstahl, die beim Zoom nicht unterschritten werden darf (z.B. 1000)
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.fitExtent = function (extent, opt_options)
{
    opt_options = opt_options || {};

	try
	{
		if( extent )
		{
            this.getMap().fit(extent, {
                projection: opt_options.projection,
                constrain_resolution: opt_options.constrain_resolution,
                minscale: opt_options.minscale
            });
		}
		else
		{
			throw new gc.gis.error.GCError('Ausdehnung ist nicht definiert.');
		}
	}
	catch( err )
	{
		gc.gis.iface.log.error( this, err );
	}

	return this;
};

/**
 * Passt den Kartenausschnitt anhand einer Geometrie an.
 *
 * @public
 * @param {gc.gis.geom.GCGeometry} geometry
 * @param {Object} [opt_options] Aufrufoptionen
 * @param {boolean} [opt_options.constrain_resolution] die Auflösungen des Kartenfensters (im Prinzip verfügbare Maßstäbe)
 *   berücksichtigen. Es wird zur passenden Auflösung gezoomt.
 * @param {int} [opt_options.minscale] Maßstabstahl, die beim Zoom nicht unterschritten werden darf (z.B. 1000), sofern
 *   der aktuelle Maßstab nicht bereits unter der angegeben Zahl liegt
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.fitGeometry = function (geometry, opt_options)
{
    opt_options = opt_options || {};
    try
    {
        if(geometry && geometry instanceof gc.gis.geom.GCGeometry && geometry.getImpl())
        {
            this.getMap().fit(geometry.getImpl(), {
                projection: geometry.getCRS(),
                constrain_resolution: opt_options.constrain_resolution,
				minscale: opt_options.minscale
            });
        }
        else
        {
            throw new gc.gis.error.GCError('Ungültige Geometrie übergeben.');
        }
    }
    catch( err )
    {
        gc.gis.iface.log.error( this, err );
    }

    return this;
};

/**
 * Bewegt den Kartenausschnitt zu den übergebenen Koordinaten.
 *
 * @public
 * @param {number[]} position Zielkoordinaten
 * @param {string} [opt_projection] Optionale Projektion der übergebenen
 *   Koordinaten. Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.panTo = function( position, opt_projection )
{
	this.getMap().panTo( position, opt_projection );

	return this;
};

/**
 * Gibt die Größe der Karte (Breite/Höhe) im DOM in Pixel zurück.
 *
 * @public
 * @abstract
 * @method
 * @return {gc.gis.map.GCMapSize} Breite/Höhe der Karte in Pixeln
 */
gc.gis.map.GCMapView.prototype.getSize = function()
{
	return this.getMap().getSize();
};

/**
 * Gibt den aktuellen Rotationswinkel der Karte in Grad zurück.
 *
 * @public
 * @return {number} Rotationswinkel der Karte
 */
gc.gis.map.GCMapView.prototype.getRotation = function()
{
	return this.getMap().getRotation();
};

/**
 * Setzt den Rotationswinkel der Karte.
 *
 * @public
 * @param {number} degrees Rotationswinkel der Karte
 * @return {gc.gis.map.GCMapView} this
 * @fires gc.gis.map.GCMapEvent#change:rotation
 */
gc.gis.map.GCMapView.prototype.setRotation = function( degrees )
{
	this.getMap().setRotation( degrees );

	return this;
};

/**
 * Gibt eine Liste vordefinierter Maßstäbe zurück.
 *
 * @public
 * @return {gc.gis.map.GCMapScale[]}
 */
gc.gis.map.GCMapView.prototype.getAvailableScales = function()
{
	if( !this._available_scales )
	{
		/**
		 * @type {gc.gis.map.GCMapScale[]}
		 * @protected
		 */
		this._available_scales = [
			{
				label: '1:100',
				value: 100
			},
			{
				label: '1:250',
				value: 250
			},
			{
				label: '1:500',
				value: 500
			},
			{
				label: '1:1.000',
				value: 1000
			},
			{
				label: '1:2.500',
				value: 2500
			},
			{
				label: '1:5.000',
				value: 5000
			},
			{
				label: '1:10.000',
				value: 10000
			},
			{
				label: '1:25.000',
				value: 25000
			},
			{
				label: '1:50.000',
				value: 50000
			},
			{
				label: '1:100.000',
				value: 100000
			},
			{
				label: '1:250.000',
				value: 250000
			},
			{
				label: '1:500.000',
				value: 500000
			},
			{
				label: '1:1.000.000',
				value: 1000000
			},
			{
				label: '1:2.500.000',
				value: 2500000
			},
			{
				label: '1:5.000.000',
				value: 5000000
			}
		];
	}

	return this._available_scales;
};

/**
 * Gibt eine Liste der verfügbaren Kartenprojektionen zurück.
 *
 * @public
 * @return {gc.gis.map.GCMapProjection[]}
 */
gc.gis.map.GCMapView.prototype.getAvailableProjections = function()
{
	// Projektionen - Fallback-Liste
	if( !this._available_projections )
	{
		/**
		 * @type {Array<Object<string,string>>} {code: string,label: string}
         * @protected
         */
		this._available_projections = [
			{
				code: 'EPSG:4326',
				label: 'WGS84'
			},
			{
				code: 'EPSG:3857',
				label: 'WGS84 / Pseudo Mercator'
			},
			{
				code: 'EPSG:900913',
				label: 'WGS84 / Pseudo Mercator (legacy)'
			},
			{
				code: 'EPSG:25832',
				label: 'ETRS89 / UTM zone 32N'
			},
			{
				code: 'EPSG:25833',
				label: 'ETRS89 / UTM zone 33N'
			},
			{
				code: 'EPSG:31467',
				label: 'DHDN / Gauss-Kruger zone 3'
			},
			{
				code: 'EPSG:31468',
				label: 'DHDN / Gauss-Kruger zone 4'
			},
			{
				code: 'EPSG:2398',
				label: 'Pulkovo 1942(83) / Gauss-Kruger zone 4'
			},
			{
				code: 'EPSG:4647',
				label: 'ETRS89 / UTM zone N32'
			},
			{
				code: 'EPSG:5650',
				label: 'ETRS89 / UTM zone 33N (zE-N)'
			}
		];
	}
	return this._available_projections;
};

/**
 * Gibt die Bezeichnung einer verfügbaren Kartenprojektionen zurück.
 *
 * @public
 * @return {string}
 */
gc.gis.map.GCMapView.prototype.getAvailableProjectionLabel = function(code)
{
	for (var i = 0, ii = this._available_projections.length; i < ii; i++)
	{
		if (this._available_projections[i].code
			&& this._available_projections[i].code.toUpperCase() === code.toUpperCase())
		{
			return this._available_projections[i].label;
		}
	}

	return code.toUpperCase();
};

/**
 * Gibt die aktuelle Kartenprojektion zurück.
 *
 * @public
 * @return {string} z.B.: 'EPSG:4326'
 */
gc.gis.map.GCMapView.prototype.getProjection = function()
{
	return this.getMap().getProjection();
};

/**
 * Setzt die aktuelle Kartenprojektion.
 *
 * @public
 * @param {string} projection neues Koordinatenreferenzsystem
 * @return {gc.gis.map.GCMapView} <code>this</code>
 * @fires gc.gis.map.GCMapEvent#change:projection
 */
gc.gis.map.GCMapView.prototype.setProjection = function( projection )
{
	var old_value = this.getProjection();
	this.getMap().setProjection( projection );

	gc.gis.iface.log.info( this, 'Kartenprojektion gesetzt: '+ projection );

	this._fireEvent( gc.gis.map.GCMapEvent.PROJECTION_CHANGED, {
		oldValue: old_value,
		newValue: projection
    });

	return this;
};

/**
 * Gibt den Mittelpunkt des aktuellen Kartenausschnitts zurück.
 *
 * @public
 * @return {number[]}
 */
gc.gis.map.GCMapView.prototype.getCenter = function()
{
	return this.getMap().getCenter();
};

/**
 * Gibt das aktuelle Zoom-Level der Karte zurück.
 * <em>Hinweis: bei "ungeraden" Zoom-Levels, hervorgerufen durch
 * Maßstabsänderungen, wird <code>undefined</code> zurückgegeben.
 * Zuverlässigere Zoom-Informationen sollten über
 * {@link gc.gis.map.GCMapView#getScale} besorgt werden.</em>
 *
 * @public
 * @return {number} Integer
 * @see gc.gis.map.GCMapView#getScale
 */
gc.gis.map.GCMapView.prototype.getZoom = function()
{
	return this.getMap().getZoom();
};

/**
 * Zoomt den Kartenausschnitt zum nächsthöheren Zoom-Level.
 *
 * @public
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.zoomIn = function()
{
	this.getMap().zoomIn();

	return this;
};

/**
 * Zoomt den Kartenausschnitt zum nächstniedrigeren Zoom-Level.
 *
 * @public
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.zoomOut = function()
{
	this.getMap().zoomOut();

	return this;
};

/**
 * Gibt die aktuelle Maßstabszahl der Karte zurück.
 *
 * @public
 * @return {number}
 */
gc.gis.map.GCMapView.prototype.getScale = function()
{
	return this.getMap().getScale();
};

/**
 * Setzt die aktuelle Maßstabszahl der Karte.
 *
 * @public
 * @param {number} scale
 * @return {gc.gis.map.GCMapView} this
 * @fires gc.gis.map.GCMapEvent#change:scale
 */
gc.gis.map.GCMapView.prototype.setScale = function( scale )
{
	gc.gis.iface.log.info(this, 'Setze Kartenmaßstab 1 : ' + scale.toLocaleString());

	var old_value = this.getScale();
	this.getMap().setScale( scale );
	this._fireEvent( gc.gis.map.GCMapEvent.SCALE_CHANGED, {
		oldValue: old_value,
		newValue: scale
	});

	return this;
};

/**
 * Gibt den maximalen Kartenausschnitt in der aktuellen Projektion zurück.
 *
 * @public
 * @method
 *
 * @param {string} [opt_projection] Zielprojektion
 * @return {gc.gis.map.GCMapExtent}
 */
gc.gis.map.GCMapView.prototype.getMaxViewportExtent = function(opt_projection)
{
	if( !this._max_extent || !this.getMap() )
	{
		return this._max_extent;	// FIXME: transformation nicht verfügbar -> OLMap._map ist initial nicht verfügbar, da ol.View vor ol.Map initialisiert werden muss!
	}
	var source_crs = gc.gis.iface.config.defaults.defaultCRS;
    var target_crs = opt_projection || this.getProjection();
    return this.transformExtent( this._max_extent, source_crs, target_crs );
};

/**
 * Gibt den Extent des aktuellen Kartenausschnitts zurück.
 *
 * @public
 * @return {gc.gis.map.GCMapExtent}
 */
gc.gis.map.GCMapView.prototype.getViewportExtent = function () {
	return this.getMap().getViewportExtent();
};

/**
 * Gibt eine Objektrepräsentation des aktuellen Kartenausschnitts zurück.
 *
 * @public
 * @return {gc.gis.map.GCMapViewport}
 */
gc.gis.map.GCMapView.prototype.getViewport = function()
{
	return this.getMap().getViewport();
};

/**
 * Ersetzt den aktuellen Viewport der Karte.
 *
 * @public
 * @param {gc.gis.map.GCMapViewport} viewport
 * @return {gc.gis.map.GCMapView} this
 * @param {boolean} [animate=true] Flag, ob der Viewport mit Animation geändert werden soll
 * @fires gc.gis.map.GCMapEvent#change:viewport
 */
gc.gis.map.GCMapView.prototype.setViewport = function(viewport, animate)
{
	if (viewport)
	{
		this.getMap().setViewport(viewport, animate);
	}

	return this;
};

/**
 * Zeigt einen Marker auf der Karte an.
 *
 * @public
 * @param {string} message Markertext
 * @param {number[]} position Markerposition
 * @param {Object} [opt_options] optionale Optionen
 * @param {string} [opt_options.projection] Optionale Projektion der übergebenen Koordinaten.
 *      Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @param {function} [opt_options.close] Callback, die beim Schließen des Markers aufgerufen wird
 * @param {function} [opt_options.enter] Callback, die beim Betreten des Markers mit der Maus aufgerufen wird
 * @param {function} [opt_options.leave] Callback, die beim Verlassen des Markers mit der Maus aufgerufen wird
 * @return {gc.gis.map.GCMapView} this
 * @throws {gc.gis.error.GCOutOfMaxExtentError}
 */
gc.gis.map.GCMapView.prototype.showMarker = function(message, position, opt_options)
{
	this.getMap().showMarker(message, position, opt_options);

	return this;
};

/**
 * Zeigt eine Vektorgeometrie auf der Karte an.
 *
 * @public
 * @param {*} geometry Geometrie
 * @param {string} format Formatstring der Geometrie
 * @param {Object} [opt_options] Optionale Optionen
 * @param {string} [opt_options.projection] Projektion der übergebenen Geometrie
 *                                          Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @param {boolean} [opt_options.clear=false] Flag, ob bereits vorhandene Hervorhebungen gelöscht werden sollen
 * @return {gc.gis.map.GCMapView} this
 * @throws {gc.gis.error.GCOutOfMaxExtentError}
 */
gc.gis.map.GCMapView.prototype.highlightGeometry = function(geometry, format, opt_options)
{
	this.getMap().highlightGeometry(geometry, format, opt_options);
	return this;
};

/**
 * Zeigt eine Vektorgeometrie auf der Karte inkl. eines Markers an.
 *
 * @public
 * @abstract
 * @method
 * @param {*} geometry Geometrie
 * @param {string} format Formatstring der Geometrie
 * @param {string} message Markertext
 * @param {Object} [opt_options] Optionale Optionen
 * @param {string} [opt_options.projection] Projektion der übergebenen Geometrie
 *          Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @param {boolean} [opt_options.clear=true] Flag, ob bereits vorhandene Hervorhebungen gelöscht werden sollen
 * @param {number[]} [opt_options.markerPosition] Markerposition
 * @param {number[]} [opt_options.markerProjection] Projektion der übergebenen Markerposition
 *          Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @throws {gc.gis.error.GCOutOfMaxExtentError}
 */
gc.gis.map.GCMapView.prototype.highlightGeometryWithMarker = function(geometry, format, message, opt_options)
{
	this.getMap().highlightGeometryWithMarker(geometry, format, message, opt_options);
	return this;
};

/**
 * Setzt die aktuellen Hervorhebungen zurück.
 *
 * @public
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.clearHighlighting = function ()
{
	this.getMap().clearHighlighting();
	return this;
};

/**
 * Zeigt einen Tooltip am Mauszeiger innerhalb der Karte an.
 *
 * @public
 * @param {string} text Tooltiptext
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.showMouseTooltip = function(text)
{
	this.getMap().showMouseTooltip(text);
	return this;
};

/**
 * Blendet den aktuell angezeigten Tooltip am Mauszeiger aus.
 *
 * @public
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.disableMouseTooltip = function()
{
	this.getMap().disableMouseTooltip();
	return this;
};

/**
 * Prüft, ob die übergebene Geometrie mit dem eingestellten max. Extent eine Überschneidung hat. Sofern kein max. Extent
 * eingestellt ist, wird stets <code>true</code> zurückgegeben.
 *
 * @param {gc.gis.geom.GCGeometry} geometry
 * @return {boolean}
 */
gc.gis.map.GCMapView.prototype.intersectsMaxExtent = function(geometry)
{
	return this.getMap().intersectsMaxExtent(geometry);
};

/**
 * Wandelt Pixelkoordinaten in Weltkoordinaten der aktuellen Projektion um.
 *
 * @public
 * @param {number} pixel
 * @return {number}
 */
gc.gis.map.GCMapView.prototype.pixelToWorld = function( pixel )
{
	return this.getMap().pixelToWorld( pixel );
};

/**
 * Wandelt Weltkoordinaten der aktuellen Projektion in Pixelkoordinaten um.
 *
 * @public
 * @param {number} coordinate
 * @return {number}
 */
gc.gis.map.GCMapView.prototype.worldToPixel = function( coordinate )
{
	return this.getMap().worldToPixel( coordinate );
};

/**
 * Aktiviert die Fangfunktion.
 *
 * @public
 */
gc.gis.map.GCMapView.prototype.enableSnap = function()
{
	var ctrl = this.getControlByType(gc.gis.control.OLSnap);

	if (ctrl)
	{
		ctrl.enable(true);
	}
};

/**
 * Deaktiviert die Fangfunktion.
 *
 * @public
 */
gc.gis.map.GCMapView.prototype.disableSnap = function()
{
	var ctrl = this.getControlByType(gc.gis.control.OLSnap);

	if (ctrl)
	{
		ctrl.enable(false);
	}
};

/**
 * Reiht den übergeben Layer in eine virtuelle Ladewarteschlange ein.
 * Der tatsächliche Ladevorgang wird durch die Layerimplementierung gehandhabt.
 * Die Warteschlange dient ausschließlich dazu festzustellen wie viele bzw.
 * welche Layer sich gerade in einem Ladeprozess befinden.
 *
 * @private
 * @param {gc.gis.layer.GCAbstractLayer} layer
 */
gc.gis.map.GCMapView.prototype.__enqueueLayerLoading = function( layer )
{
	this._loading_queue.push( layer );
};

/**
 * Löst den übergeben Layer aus der virtuellen Ladewarteschlange ein.
 * Der tatsächliche Ladevorgang wird durch die Layerimplementierung gehandhabt.
 * Die Warteschlange dient ausschließlich dazu festzustellen wie viele bzw.
 * welche Layer sich gerade in einem Ladeprozess befinden.
 *
 * @private
 * @param {gc.gis.layer.GCAbstractLayer} layer
 */
gc.gis.map.GCMapView.prototype.__dequeueLayerLoading = function( layer )
{
	var idx = this._loading_queue.indexOf( layer );
	if( idx >= 0 )
	{
		this._loading_queue.splice( idx, 1 );
	}
};

/**
 * Gibt die Liste der aktuell ladenden Layer zurück.
 * Der tatsächliche Ladevorgang wird durch die Layerimplementierung gehandhabt.
 * Die Warteschlange dient ausschließlich dazu festzustellen wie viele bzw.
 * welche Layer sich gerade in einem Ladeprozess befinden.
 *
 * @public
 * @return {gc.gis.layer.GCAbstractLayer[]}
 */
gc.gis.map.GCMapView.prototype.getLoadingQueue = function()
{
	return this._loading_queue;
};

/**
 * Gibt die aktuell eingestellte Zeit zurück, bevor die Karte nach einer Aktion
 * aktualisiert wird.
 *
 * @public
 * @return {number} Zeit in Millisekunden
 */
gc.gis.map.GCMapView.prototype.getLoadingTimeout = function()
{
	return this._loading_timeout;
};

/**
 * Signalisiert, dass der übergebene Layer angefangen hat zu laden.
 *
 * @public
 * @param {gc.gis.layer.GCAbstractLayer[]} layers
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.onLoadingStarted = function( layers )
{
	for( var i=0; i<layers.length; i++ )
	{
		this.__enqueueLayerLoading( layers[i] );
		this._fireEvent( gc.gis.map.GCMapEvent.LAYER_LOADING_STARTED, {	// FIXME: doku: warum/wohin wird hier weitergefeuert? (wer hört zu?)
			layer: layers[i]
		});
		layers[i]._fireEvent( gc.gis.map.GCMapEvent.LAYER_LOADING_STARTED, {} );
	}

	return this;
};

/**
 * Signalisiert, dass der übergebene Layer aufgehört hat zu laden.
 *
 * @public
 * @param {gc.gis.layer.GCAbstractLayer[]} layers
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.onLoadingEnded = function( layers )
{
	for( var i=0; i<layers.length; i++ )
	{
		this.__dequeueLayerLoading( layers[i] );
		this._fireEvent( gc.gis.map.GCMapEvent.LAYER_LOADING_ENDED, {
			layer: layers[i]
		});
		layers[i]._fireEvent( gc.gis.map.GCMapEvent.LAYER_LOADING_ENDED, {} );
	}

	return this;
};

/**
 * Signalisiert, dass der übergebene Layer einen Ladefehler hatte.
 *
 * @public
 * @param {gc.gis.layer.GCAbstractLayer[]} layers
 * @param {Object} err
 * @return {gc.gis.map.GCMapView} this
 */
gc.gis.map.GCMapView.prototype.onLoadingError = function( layers, err )
{
	for( var i=0; i<layers.length; i++ )
	{
		this.__dequeueLayerLoading( layers[i] );	// Eigentlich überflüssig, aber falls mal ein finally fehlt
		if( !err )
		{
			err = new gc.gis.error.GCError(
				'Konnte Ebene nicht laden'
				+ (layers[i] && layers[i].getLabel() ? ' "'+ layers[i].getLabel() +'"' : '')
				+'.'
			);
		}
		this._fireEvent( gc.gis.map.GCMapEvent.LAYER_LOADING_ERROR, {
			layer: layers[i],
			error: err
		});
		layers[i]._fireEvent( gc.gis.map.GCMapEvent.LAYER_LOADING_ERROR, {
			error: err
		} );
		gc.gis.iface.log.error( layers[i], err );
	}

	return this;
};

